import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import { NEAR_ME_RADII_KM } from '../lib/listingPrefs';

// WO-13: "Near me" filter for card listings. Store-level: the API filters store locations by
// distance and returns the listings of stores with a location in range. The user's location comes
// from their profile or, only after they click, the browser's geolocation prompt. Coordinates are
// rounded to ~1 km before leaving the browser.
// WO-35: the setting is remembered in this browser (lib/listingPrefs.js: on/off, radius and the rounded place, until
// "Clear"), so "near me only" follows the shopper to every card and sealed page; NearMeChip puts it next to the
// listings' sort on every page (and on mobile, where the filter sidebar is a drawer).

export { NEAR_ME_RADII_KM };

export const roundCoord = (value) => Math.round(Number(value) * 100) / 100;

export const profileHasLocation = (profile) => Number.isFinite(Number(profile?.latitude))
  && Number.isFinite(Number(profile?.longitude))
  && profile?.latitude !== null && profile?.longitude !== null
  && profile?.latitude !== undefined && profile?.longitude !== undefined;

export const profilePlaceOf = (profile) => profile?.city_locality || profile?.city || profile?.region_name || 'profile location';

const DENIED_TEXT = 'Location is blocked for this site. Allow it in your browser settings, or save a location in your profile.';

// Browser geolocation, asked only after a click. Calls back with a rounded point or a shopper-readable error.
export function locateWithBrowser(onPoint, onError) {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    onError('Your browser does not share location.');
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (position) => onPoint({ lat: roundCoord(position.coords.latitude), lng: roundCoord(position.coords.longitude) }),
    (err) => onError(err && err.code === 1 ? DENIED_TEXT : 'Could not get your location. Try again.'),
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 }
  );
}

// Compact toggle next to a listings header: off -> uses the profile location, else asks the browser; on -> shows
// the radius and a clear button. `onOpenPanel` (optional) opens the full panel to change the radius or place.
export const NearMeChip = ({ profile, nearMe, onChange, status, wanted = false, className = '' }) => {
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);

  const turnOn = () => {
    setError(null);
    if (nearMe.lat !== null && nearMe.lng !== null && nearMe.label) {
      onChange({ ...nearMe, active: true });   // the place used last time
      return;
    }
    if (profileHasLocation(profile)) {
      onChange({ ...nearMe, active: true, lat: roundCoord(profile.latitude), lng: roundCoord(profile.longitude),
        label: profilePlaceOf(profile), origin: 'profile' });
      return;
    }
    setLocating(true);
    locateWithBrowser(
      (pt) => { setLocating(false); onChange({ ...nearMe, ...pt, active: true, label: 'your location', origin: 'browser' }); },
      (msg) => { setLocating(false); setError(msg); }
    );
  };

  if (nearMe.active) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-full border border-skyblue/60 bg-skyblue/10 px-3 py-1 text-xs text-cream ${className}`}
            data-testid="near-me-chip" data-active="true">
        <MapPin size={12} />
        <span>Near me · {nearMe.radiusKm} km{status === 'loading' ? ' …' : ''}</span>
        <button type="button" aria-label="Turn off near me" title="Show all listings"
                onClick={() => onChange({ ...nearMe, active: false })} className="ml-1 text-cream/70 hover:text-cream">
          <X size={12} />
        </button>
      </span>
    );
  }
  return (
    <span className={`inline-flex flex-col items-start ${className}`}>
      <button type="button" onClick={turnOn} disabled={locating} data-testid="near-me-chip" data-active="false"
              title="Show only listings from stores near you"
              className="inline-flex items-center gap-1 rounded-full border border-purple/40 px-3 py-1 text-xs text-cream/80 hover:text-cream hover:border-purple">
        <MapPin size={12} />
        {locating ? 'Locating…' : (wanted ? 'Near me (update location)' : 'Near me only')}
      </button>
      {error && <span className="mt-1 max-w-xs text-[11px] text-red-400" data-testid="near-me-error">{error}</span>}
    </span>
  );
};

export const formatDistanceKm = (km) => {
  if (!Number.isFinite(km)) return '';
  if (km < 1) return '<1 km';
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
};

const NearMeFilter = ({ open, onToggle, profile, nearMe, onChange, matchCount }) => {
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState(null);

  const hasProfileLocation = profileHasLocation(profile);
  const profilePlace = profilePlaceOf(profile);

  const useProfile = () => {
    setGeoError(null);
    onChange({
      ...nearMe,
      active: true,
      lat: roundCoord(profile.latitude),
      lng: roundCoord(profile.longitude),
      label: profilePlace,
      origin: 'profile',
    });
  };

  const useBrowser = () => {
    setGeoError(null);
    setLocating(true);
    locateWithBrowser(
      (pt) => {
        setLocating(false);
        onChange({ ...nearMe, ...pt, active: true, label: 'your current location', origin: 'browser' });
      },
      (msg) => {
        setLocating(false);
        setGeoError(msg);
      }
    );
  };

  // WO-35: Clear also forgets the remembered place (not only "off").
  const clear = () => {
    setGeoError(null);
    onChange({ ...nearMe, active: false, lat: null, lng: null, label: '', origin: null });
  };

  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-cream font-medium mb-2"
      >
        <span className="flex items-center gap-1"><MapPin size={14} /> Near Me</span>
        <span className="text-xs text-cream/60">{open ? '–' : '+'}</span>
      </button>
      {open && (
        <div className="space-y-2 text-sm">
          {nearMe.active ? (
            <div className="text-cream/80">
              Local stores within {nearMe.radiusKm} km of {nearMe.label}
              {nearMe.status === 'loading' && <span className="text-cream/60"> (loading…)</span>}
              {nearMe.status === 'ready' && <span className="text-cream/60"> ({matchCount})</span>}
              {nearMe.status === 'error' && <div className="text-red-400 text-xs mt-1">Could not load nearby stores.</div>}
            </div>
          ) : (
            <div className="text-cream/60 text-xs">
              Show only listings from stores you can visit. Your choice is remembered on this device for every card.
            </div>
          )}
          <label className="flex items-center justify-between text-cream/80">
            <span>Within</span>
            <select
              value={nearMe.radiusKm}
              onChange={(e) => onChange({ ...nearMe, radiusKm: Number(e.target.value) })}
              className="bg-dark border border-purple/30 rounded-lg px-2 py-1 text-cream text-sm"
            >
              {NEAR_ME_RADII_KM.map((km) => (
                <option key={km} value={km}>{km} km</option>
              ))}
            </select>
          </label>
          {hasProfileLocation && (
            <button
              type="button"
              onClick={useProfile}
              className={`w-full text-left px-2 py-1 rounded border ${nearMe.active && nearMe.origin === 'profile' ? 'border-purple bg-purple/20 text-cream' : 'border-purple/30 text-cream/80'}`}
            >
              Use {profilePlace}
            </button>
          )}
          <button
            type="button"
            onClick={useBrowser}
            disabled={locating}
            className={`w-full text-left px-2 py-1 rounded border ${nearMe.active && nearMe.origin === 'browser' ? 'border-purple bg-purple/20 text-cream' : 'border-purple/30 text-cream/80'}`}
          >
            {locating ? 'Locating…' : 'Use my current location'}
          </button>
          {nearMe.active && (
            <button type="button" onClick={clear} className="text-xs text-cream/60 underline">
              Clear
            </button>
          )}
          {geoError && (
            <div className="text-red-400 text-xs" data-testid="near-me-error">
              {geoError}
              {!hasProfileLocation && profile && (
                <> <Link to="/edit-profile" className="underline text-cream/80">Set a location</Link></>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NearMeFilter;
